import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import type { FilmSettings } from '@eip/engine';
import { Select, Slider, Toggle, nextDraft } from './controls';
import { filmSizeOptions, filmSizeValue, nextFilmSize, type FilmSizeChoice } from '../lib/sep-ui';
import { Section } from './Section';

const MARK_STYLES = [{ value: 'crosshair', label: 'Crosshair' }, { value: 'target', label: 'Target' }];

export function FilmPanel({ film, onChange }: { film: FilmSettings; onChange: (next: FilmSettings) => void }) {
  const set = (patch: Partial<FilmSettings>) => onChange({ ...film, ...patch });

  // Whether the size select reads 'custom' is tracked explicitly, not
  // inferred from `film.size` (`filmSizeValue` is structural): if it were
  // inferred, choosing "Custom" while a named preset is active would be a
  // no-op the instant the numbers matched that preset, and the width/height
  // inputs would never appear. Seeded from the document's own size so a
  // custom size loaded from a document (or `sepDefaults`) still shows as
  // Custom on first render.
  const [customSize, setCustomSize] = useState(() => filmSizeValue(film.size) === 'custom');
  const sizeValue = customSize ? 'custom' : filmSizeValue(film.size);

  const onSizeChange = (value: string) => {
    const choice = value as FilmSizeChoice;
    setCustomSize(choice === 'custom');
    set({ size: nextFilmSize(film.size, choice) });
  };

  // Job name commits on blur/Enter, not per keystroke — the same draft/dirty
  // pattern as LPI/index dpi (see ScreenPanel, SettingsBar): every keystroke
  // shows locally, Escape reverts, and an external change (e.g. loading a
  // persisted default) can't clobber an in-progress edit.
  const [nameDraft, setNameDraft] = useState(film.jobName);
  const nameFocused = useRef(false);
  const nameDirty = useRef(false);
  useEffect(() => { setNameDraft((prev) => nextDraft(prev, film.jobName, nameFocused.current)); }, [film.jobName]);
  const commitName = (raw: string) => { if (raw !== film.jobName) set({ jobName: raw }); };

  return (
    <Section id="film" title="Film">
      <Select label="Size" value={sizeValue} options={filmSizeOptions()} onChange={onSizeChange} />
      {customSize && film.size !== 'fit' && (<>
        <Slider label="Width" value={film.size.widthIn} min={1} max={60} step={0.5} unit="in" onChange={(v) => set({ size: { widthIn: v, heightIn: (film.size as { heightIn: number }).heightIn } })} />
        <Slider label="Height" value={film.size.heightIn} min={1} max={60} step={0.5} unit="in" onChange={(v) => set({ size: { widthIn: (film.size as { widthIn: number }).widthIn, heightIn: v } })} />
      </>)}
      <Slider label="Margin" value={film.marginIn} min={0} max={3} step={0.25} unit="in" onChange={(v) => set({ marginIn: v })} />
      <Toggle label="Registration marks" checked={film.marks} onChange={(v) => set({ marks: v })} />
      {film.marks && (<>
        <Select label="Mark style" value={film.markStyle} options={MARK_STYLES} onChange={(v) => set({ markStyle: v as FilmSettings['markStyle'] })} />
        <Slider label="Mark size" value={film.markSizeMm} min={2} max={20} step={1} unit="mm" onChange={(v) => set({ markSizeMm: v })} />
      </>)}
      {/* "Labels" said nothing about what is labelled or with what (audit #22).
          `assembleFilm` prints one caption line under each film's art —
          `plateLabel`: the job name, the ink, its number in the set, the screen
          and the date — so the toggle is named for the film and the hint lists
          what lands on it. */}
      <Toggle
        label="Label each film" hint="Prints the job name, the ink, the screen and the date under each film"
        checked={film.labels} onChange={(v) => set({ labels: v })}
      />
      <Toggle label="Mirror (emulsion-down)" checked={film.mirror} onChange={(v) => set({ mirror: v })} />
      <label className="row">Job name
        <input
          value={nameDraft}
          aria-label="Job name"
          onFocus={() => { nameFocused.current = true; }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setNameDraft(e.target.value); nameDirty.current = true; }}
          onBlur={() => { nameFocused.current = false; if (nameDirty.current) { commitName(nameDraft); nameDirty.current = false; } }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { commitName(nameDraft); nameDirty.current = false; }
            else if (e.key === 'Escape') { setNameDraft(film.jobName); nameDirty.current = false; }
          }}
        />
      </label>
    </Section>
  );
}
